import {Database} from '@tnc/shared';
import clsx from 'clsx';
import {debounce} from 'lodash';
import React, {ChangeEvent, Ref, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react';
import ReactPaginate from 'react-paginate';
import {twMerge} from 'tailwind-merge';

import {dataTableActionT, dataTableColumnT, sortOptionT} from '@/lib/types/dataTable.types';
import {ReactPaginateClickEventT, VALID_LIMITS, limitT} from '@/lib/types/pagination.types';
import {ColumnSelectionT, CombinedColumnsT, FilterT, SelectedColumnsKeysT} from '@/lib/types/query.types';

import {getSortOptions} from '@/lib/constants';

import {useDataQuery} from '@/hooks';

import {LoadingSpinningLogoComponent} from '@/components/loadingSpinningLogoComponent';

export type DataTableRefT = {
  refetch: () => void;
  setLimit: (limit: limitT) => void;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
};

// First, update the types to support different control types
type BaseCustomFilter = {
  label: string;
  color?: string;
};

type ButtonCustomFilter = BaseCustomFilter & {
  type: 'button';
  onClick: () => void;
  active?: boolean;
};

type DropdownCustomFilter = BaseCustomFilter & {
  type: 'dropdown';
  options: {label: string; value: string | number; selected?: boolean}[];
  onChange: (value: string | number) => void;
};

type InputCustomFilter = BaseCustomFilter & {
  type: 'input';
  placeholder?: string;
  onChange: (value: string) => void;
  value?: string;
};

type CustomFilterT = ButtonCustomFilter | DropdownCustomFilter | InputCustomFilter;

export type DataTablePropsT<T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[] = ['*']> = {
  // Data
  table: T;
  columns: C;
  columnsDisplay: dataTableColumnT<T, CombinedColumnsT<T, C>>[];
  // Options
  actionsDisplay?: dataTableActionT<T, CombinedColumnsT<T, C>>[];
  limit?: limitT;
  filters?: FilterT<T, C>[];
  sortOptions?: sortOptionT<T>[];
  searchColumn?: SelectedColumnsKeysT<T, C>;
  enabled?: boolean;
  authOverride?: boolean;
  // Display
  title?: string;
  className?: string;
  addButtonLabel?: string;
  // Display Optionals
  raw?: boolean;
  hideSearch?: boolean;
  hideRefresh?: boolean;
  hideSort?: boolean;
  hideTitle?: boolean;
  hideAdd?: boolean;
  hideShowing?: boolean;
  hideLimit?: boolean;
  hideLimitIcon?: boolean;
  hidePagination?: boolean;
  hideLoading?: boolean;
  preventScrolling?: boolean;
  noResultsMessage?: string;
  // Functions
  onRefresh?: () => void;
  onRefetch?: (refetch: () => void) => void;
  onSearch?: (value: string, debounceTime?: number) => void;
  onAdd?: () => void;
  onSort?: (option: sortOptionT<T>) => void;
  onSetLimit?: (limit: limitT) => void;
  onGoToPage?: (page: number) => void;
  onPrev?: () => void;
  onNext?: () => void;
  customFilterButtons?: CustomFilterT[];

  staleTime?: number;
  refetchInterval?: number;
};

function DataTableComponent<T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[] = ['*']>(
  props: DataTablePropsT<T, C>,
  ref: Ref<DataTableRefT>
) {
  const {
    title,
    table,
    columns,
    limit: _limit = 5,
    columnsDisplay: tableColumns = [],
    actionsDisplay: tableActions,
    sortOptions = getSortOptions<T>(table),
    searchColumn,
    hideSearch = false,
    hideRefresh = false,
    hideSort = false,
    hideTitle = false,
    hideAdd = false,
    hideShowing = false,
    hideLimit = false,
    hideLimitIcon = false,
    hidePagination = false,
    hideLoading = false,
    raw = false,
    className,
    addButtonLabel = 'Create New',
    filters,
    preventScrolling = false,
    noResultsMessage = 'No results found',
    enabled = true,
    authOverride = false,
    onRefresh,
    onSearch,
    onAdd,
    onSort,
    onSetLimit,
    onGoToPage,
    onPrev,
    onNext,
    customFilterButtons = [],
    staleTime,
    refetchInterval,
  } = props;

  const defaultSortOption = sortOptions.find(option => option.default) ?? sortOptions[0];

  // State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<limitT>(_limit);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSortOption, setSortSelectedOption] = useState<sortOptionT<T>>(defaultSortOption);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null!);

  // Data Hook
  const {
    data: {data, currentPage, totalPages} = {data: [], totalPages: 0, currentPage: 1},
    isLoading,
    refetch,
  } = useDataQuery({
    table,
    columns,
    page,
    limit,
    searchTerm,
    sortOption: selectedSortOption,
    searchColumn,
    filters,
    enabled,
    authOverride,
    ...(staleTime !== undefined && {staleTime}),
    ...(refetchInterval !== undefined && {refetchInterval}),
  });

  // Reactivity
  useImperativeHandle(ref, () => ({
    refetch,
    setLimit,
    goToPage,
    nextPage: handleNext,
    prevPage: handlePrev,
  }));

  // Pagination
  const handlePrev = useCallback(() => {
    setPage(prev => Math.max(prev - 1, 1));
    onPrev?.();
    if (!preventScrolling) window.scrollTo({top: 0, behavior: 'instant'});
  }, [onPrev, preventScrolling]);

  const handleNext = useCallback(() => {
    setPage(prev => Math.min(prev + 1, totalPages));
    onNext?.();
    if (!preventScrolling) window.scrollTo({top: 0, behavior: 'instant'});
  }, [onNext, preventScrolling, totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      setPage(Math.min(Math.max(1, page), totalPages));
      onGoToPage?.(page);
      if (!preventScrolling) window.scrollTo({top: 0, behavior: 'instant'});
    },
    [onGoToPage, preventScrolling, totalPages]
  );

  // Functions
  const handleSort = useCallback(
    (option: sortOptionT<T>) => {
      setSortSelectedOption(option);
      onSort?.(option);
      goToPage(1);
    },
    [goToPage, onSort]
  );

  const handleRefresh = useCallback(() => {
    searchInputRef.current.value = '';
    setSearchTerm('');
    setSortSelectedOption(defaultSortOption);
    refetch();
    goToPage(1);
    onRefresh?.();
  }, [goToPage, onRefresh, refetch, defaultSortOption]);

  const handleSetLimit = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      // Typeguard to ensure that the value is a valid limit
      const newLimit = parseInt(e.target.value, 10) as limitT;
      if (!VALID_LIMITS.includes(newLimit)) return;

      setLimit(newLimit);
      onSetLimit?.(newLimit);
      goToPage(1);
    },
    [goToPage, onSetLimit]
  );

  // The SyntheticEvent event (event) and the currently selected page (selected) are also
  // available in the ReactPaginateClickEventT type, but are is not used here
  const handlePageClick = useCallback(
    ({isActive, isBreak, isNext, isPrevious, nextSelectedPage}: ReactPaginateClickEventT) => {
      // If the click was on the break or the active page, do nothing
      if (isBreak || isActive) return;

      // Handle the next and previous buttons
      if (isNext) return handleNext();
      if (isPrevious) return handlePrev();

      // Handle the page click (the nextSelectedPage value is 0-indexed so we need to add 1)
      goToPage(nextSelectedPage + 1);
    },
    [goToPage, handleNext, handlePrev]
  );

  // Function to sor the sort options
  const sortSortOptions = (options: sortOptionT<T>[]) => {
    // Find the default sort option
    const defaultOption = options.find(option => option.default);

    // Remove the default sort option from the array
    const remainingOptions = options.filter(option => !option.default);

    // Sort the remaining options by the desired key (e.g., label)
    remainingOptions.sort((a, b) => a.label.localeCompare(b.label));

    // Combine the default option with the sorted array
    const newOptions = defaultOption ? [defaultOption, ...remainingOptions] : remainingOptions;

    // Set the selected option
    newOptions.forEach(option => {
      option.selected = option.label === selectedSortOption.label;
    });

    return newOptions;
  };

  // Search Functions
  const searchData = useCallback(
    async (searchTerm: string) => {
      setSearchTerm(searchTerm);
      onSearch?.(searchTerm);
      goToPage(1);

      // Reset if the search term is empty
      if (searchTerm === '') {
        goToPage(1);
        refetch();
      }
    },
    [goToPage, onSearch, refetch]
  );

  // Create a memoized debounced version of the search function
  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        // Call the searchData function but don't worry about the return type
        searchData(searchTerm).catch(error => console.error(error));
      }, 750),
    [searchData]
  );

  // Wrapper function to maintain the type consistency and handle debounce time
  const handleSearchData = (
    searchTerm: string,
    debounceTime?: number
  ): Promise<Database['public']['Tables'][T]['Row'][]> => {
    if (debounceTime !== undefined && debounceTime !== 750) {
      // If a custom debounce time is provided, create a new debounced function
      debounce((term: string) => {
        searchData(term).catch(error => console.error(error));
      }, debounceTime)(searchTerm);
    } else {
      // Use the memoized debounced function
      debouncedSearch(searchTerm);
    }
    // Return a resolved promise to satisfy the type requirement
    return Promise.resolve([]);
  };

  // Default render function for columns that do not have a custom render function
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultRender = (row: any, field: string) => {
    const fieldParts = field.split('.');
    let value = row;
    for (const part of fieldParts) {
      value = value[part];
      if (value === undefined) {
        return '';
      }
    }
    return String(value);
  };

  // Then in the render section, update the custom filter buttons implementation
  const renderCustomFilter = (filter: CustomFilterT, index: number) => {
    const baseClassName = `!m-0 !h-[1.75rem] text-[0.8rem]`;
    const colorClass = filter.color || 'primary';

    switch (filter.type) {
      case 'button':
        return (
          <button
            key={index}
            onClick={filter.onClick}
            className={`ti-btn ti-btn-icon ti-btn-wave ${
              filter.active ? `!bg-${colorClass} !text-white` : `bg-${colorClass}/10 text-${colorClass}`
            } hover:border-${colorClass} hover:bg-${colorClass} hover:text-white ${baseClassName} !w-auto hover:text-white`}
          >
            {filter.label}
          </button>
        );

      case 'dropdown':
        return (
          <div key={index} className="hs-dropdown ti-dropdown">
            <button
              className={`ti-btn ti-btn-wave bg-${colorClass}/10 text-${colorClass} hover:bg-${colorClass} hover:text-white ${baseClassName} !px-2`}
            >
              {filter.label} <i className="ri-arrow-down-s-line ms-1 inline-block align-middle"></i>
            </button>
            <ul className="hs-dropdown-menu ti-dropdown-menu hidden" role="menu">
              {filter.options.map((option, optionIndex) => (
                <li key={optionIndex}>
                  <button
                    onClick={() => filter.onChange(option.value)}
                    className="ti-dropdown-item !px-[0.9375rem] !py-2 !text-[0.8125rem] !font-medium"
                  >
                    {option.label}
                    {option.selected && <i className="ri-check-line ms-2 text-primary"></i>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );

      case 'input':
        return (
          <div key={index} className="search-form">
            <input
              className={`ti-form-control form-control-sm ${baseClassName}`}
              type="text"
              placeholder={filter.placeholder || filter.label}
              value={filter.value}
              onChange={e => filter.onChange(e.target.value)}
            />
          </div>
        );
    }
  };

  // TODO: Add skeleton loader for table
  if (isLoading && !hideLoading) return <LoadingSpinningLogoComponent />;

  // Determine the maximum number of columns, including the action column if present
  const maxColumns =
    data.length === 0
      ? 1
      : Math.max(...tableColumns.map(column => column.header.length)) +
        (tableActions && tableActions.length > 0 ? 1 : 0);
  const gridColsClass = `grid-cols-${Math.min(maxColumns, 12)}`; // Limiting to a maximum of 12 columns
  const maxWidthClass = 'max-w-xs'; // Tailwind CSS class for maximum width (e.g., max-w-xs for max-width: 20rem)

  return (
    <div className={twMerge(clsx(`grid ${gridColsClass} gap-x-6`, className))}>
      <div className="col-span-12 xl:col-span-12">
        <div className="box">
          {!raw && !(hideTitle && hideRefresh && hideSearch && hideSort && hideAdd) && (
            <div className="box-header flex-wrap justify-between">
              {!hideTitle && title ? <h1 className="box-title">{title}</h1> : <div className="grow"></div>}
              <div className="flex items-stretch gap-2">
                {/* Custom Filter Buttons */}
                {customFilterButtons.map((filter, index) => renderCustomFilter(filter, index))}
                {/* Refresh Button */}
                {!hideRefresh && (
                  <button
                    onClick={handleRefresh}
                    className="ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] bg-primary/10 text-[0.8rem] text-primary hover:border-primary hover:bg-primary hover:text-white"
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                )}
                {/* Search Input */}
                {!hideSearch && (
                  <div className="me-3">
                    <div className="search-form h-full">
                      <input
                        className="ti-form-control form-control-sm h-full"
                        type="text"
                        placeholder="Search Here"
                        aria-label=".form-control-sm example"
                        onChange={() => handleSearchData(searchInputRef.current.value)}
                        ref={searchInputRef}
                      />
                    </div>
                  </div>
                )}
                {/* Sort Dropdown */}
                {!hideSort && (
                  <div className="hs-dropdown ti-dropdown">
                    <button className="ti-btn ti-btn-primary !m-0 !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white">
                      Sort By <i className="ri-arrow-down-s-line ms-1 inline-block align-middle"></i>
                    </button>
                    <ul className="hs-dropdown-menu ti-dropdown-menu hidden" role="menu">
                      {sortSortOptions(sortOptions).map((option, index) => (
                        <li key={index}>
                          <button
                            onClick={() => handleSort(option)}
                            className="ti-dropdown-item !px-[0.9375rem] !py-2 !text-[0.8125rem] !font-medium"
                          >
                            {/* TODO: Fix width in dropdown to full width */}
                            {option.label}
                            {option.selected && <i className="ri-check-line ms-2 text-primary"></i>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Add Button */}
                {!hideAdd && (
                  <div>
                    <button
                      onClick={onAdd}
                      className="ti-btn ti-btn-primary !m-0 h-full !gap-0 !bg-primary !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                    >
                      {addButtonLabel}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="box-body">
            <div className="table-responsive">
              <table className="table-hover table-bordered table min-w-full whitespace-nowrap">
                <thead>
                  <tr>
                    {tableColumns?.map((column, index) => (
                      <th key={index} className="text-start">
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {/* No results :( */}
                {data.length === 0 && (
                  <tbody>
                    <tr className="border">
                      <td colSpan={tableColumns.length}>
                        <div className="text-center font-bold">{noResultsMessage}</div>
                      </td>
                    </tr>
                  </tbody>
                )}
                {/* We have results */}
                {data.length > 0 && (
                  <tbody>
                    {data.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="border border-solid border-inherit hover:bg-gray-100 dark:border-defaultborder/10 dark:hover:bg-light"
                      >
                        {tableColumns?.map((column, colIndex) => (
                          <td className={`overflow-hidden ${maxWidthClass}`} key={colIndex}>
                            {column.render ? column.render(row) : defaultRender(row, column.field)}
                          </td>
                        ))}
                        {tableActions && tableActions.some(action => action.permission(row)) && (
                          <td>
                            <div className="flex flex-row items-center justify-center gap-2 text-[0.9375rem]">
                              {tableActions
                                .filter(action => action.isShown?.(row) ?? true)
                                .map(
                                  (action, actionIndex) =>
                                    action.permission(row) && (
                                      <button
                                        key={actionIndex}
                                        onClick={() => action.handler(row)}
                                        className={`ti-btn ti-btn-icon ti-btn-wave !m-0 !h-[1.75rem] !w-[1.75rem] !gap-0 text-[0.8rem] bg-${action.color}/10 text-${action.color} hover:bg-${action.color} hover:text-white hover:border-${action.color}`}
                                      >
                                        <i className={action.icon}></i>
                                      </button>
                                    )
                                )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
          {!raw && !(hideShowing && hideLimitIcon && hideLimit && hidePagination) && (
            <div className="box-footer">
              <div className="flex items-center justify-between">
                {!(hideShowing && hideLimitIcon && hideLimit) && (
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-400">
                    {!hideShowing && (
                      <div>
                        Showing {(currentPage - 1) * _limit + 1} to {Math.min(data?.length ?? 0)} of{' '}
                        {totalPages * limit} entries
                      </div>
                    )}
                    {!hideLimitIcon && <i className="bi bi-arrow-right ms-2 font-semibold"></i>}
                    {!hideLimit && (
                      <>
                        <div>
                          <select
                            className="ti-form-control form-select-sm form-select ml-4"
                            defaultValue={_limit}
                            onChange={handleSetLimit}
                          >
                            {VALID_LIMITS.map((value, index) => (
                              <option key={index} value={value}>
                                {value}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {!hidePagination && totalPages > 0 && (
                  <div className="pagination-style-4">
                    <ReactPaginate
                      forcePage={currentPage - 1}
                      onClick={handlePageClick}
                      pageCount={Math.max(1, totalPages)}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={2}
                      renderOnZeroPageCount={null}
                      className="ti-pagination select-none gap-1"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      activeLinkClassName="active"
                      breakLabel={'...'}
                      breakLinkClassName="page-item"
                      nextClassName={clsx({'hidden disabled': currentPage === totalPages})}
                      nextLinkClassName="page-link"
                      nextLabel={'Next'}
                      previousClassName={clsx({'hidden disabled': currentPage === 1})}
                      previousLinkClassName="page-link"
                      previousLabel={'Prev'}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DataTable = React.forwardRef(DataTableComponent) as unknown as <
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
>(
  props: DataTablePropsT<T, C> & React.RefAttributes<DataTableRefT>
) => React.ReactElement | null;
